import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useLocation } from 'react-router-dom';

// Hook to fetch popup data from Contentful
const usePopup = (currentPage) => {
  const [popup, setPopup] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPopup = async () => {
      try {
        const spaceId = process.env.REACT_APP_CONTENTFUL_SPACE_ID;
        const accessToken = process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN;
        
        if (spaceId && accessToken) {
          const response = await fetch(
            `https://cdn.contentful.com/spaces/${spaceId}/environments/master/entries?content_type=popup&fields.active=true`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );
          
          if (response.ok) {
            const data = await response.json();
            
            // Map assets for images
            const assets = {};
            if (data.includes?.Asset) {
              data.includes.Asset.forEach(asset => {
                assets[asset.sys.id] = `https:${asset.fields.file.url}`;
              });
            }
            
            // Find popup that matches current page
            const matchingPopup = data.items?.find(item => {
              const targetPages = item.fields.targetPages || [];
              // Check if popup should show on this page
              return targetPages.includes(currentPage) || 
                     targetPages.includes('all') ||
                     (currentPage === '/' && targetPages.includes('home'));
            });
            
            if (matchingPopup) {
              setPopup({
                id: matchingPopup.sys.id,
                title: matchingPopup.fields.title,
                content: matchingPopup.fields.content,
                buttonText: matchingPopup.fields.buttonText || 'Learn More',
                buttonUrl: matchingPopup.fields.buttonUrl,
                image: matchingPopup.fields.image?.sys?.id 
                  ? assets[matchingPopup.fields.image.sys.id] 
                  : null,
                dismissable: matchingPopup.fields.dismissable !== false,
              });
            }
          }
        }
        
        setIsLoading(false);
      } catch (error) {
        console.warn('Failed to fetch popup:', error);
        setIsLoading(false);
      }
    };

    fetchPopup();
  }, [currentPage]);

  return { popup, isLoading };
};

// Popup Component
export const Popup = () => {
  const location = useLocation();
  const currentPage = location.pathname;
  const { popup, isLoading } = usePopup(currentPage);
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenDismissed, setHasBeenDismissed] = useState(false);

  // Check if popup was already dismissed for this session
  useEffect(() => {
    if (popup && !hasBeenDismissed) {
      const dismissedPopups = sessionStorage.getItem('dismissedPopups');
      const dismissed = dismissedPopups ? JSON.parse(dismissedPopups) : [];
      
      if (!dismissed.includes(popup.id)) {
        // Show popup after a short delay
        const timer = setTimeout(() => setIsVisible(true), 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [popup, hasBeenDismissed]);

  const handleDismiss = () => {
    if (popup) {
      // Remember that this popup was dismissed
      const dismissedPopups = sessionStorage.getItem('dismissedPopups');
      const dismissed = dismissedPopups ? JSON.parse(dismissedPopups) : [];
      dismissed.push(popup.id);
      sessionStorage.setItem('dismissedPopups', JSON.stringify(dismissed));
    }
    setIsVisible(false);
    setHasBeenDismissed(true);
  };

  if (isLoading || !popup || !isVisible) {
    return null;
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={popup.dismissable ? handleDismiss : undefined}
            className="fixed inset-0 z-50 bg-black/50"
          />
          
          {/* Popup Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden pointer-events-auto">
              {/* Close Button */}
              {popup.dismissable && (
                <button
                  onClick={handleDismiss}
                  className="absolute top-4 right-4 p-2 rounded-full bg-white/80 hover:bg-white transition-colors z-10"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              )}
              
              {/* Image */}
              {popup.image && (
                <div className="aspect-video overflow-hidden">
                  <img
                    src={popup.image}
                    alt={popup.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              {/* Content */}
              <div className="p-6">
                {popup.title && (
                  <h2 className="font-heading text-2xl font-bold text-brand-primary mb-3">
                    {popup.title}
                  </h2>
                )}
                
                {popup.content && (
                  <p className="text-slate-600 mb-6">
                    {popup.content}
                  </p>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-3">
                  {popup.buttonUrl && (
                    <a
                      href={popup.buttonUrl}
                      target={popup.buttonUrl.startsWith('http') ? '_blank' : '_self'}
                      rel="noopener noreferrer"
                      className="flex-1 text-center px-6 py-3 bg-brand-secondary text-white rounded-full font-semibold hover:bg-brand-secondary/90 transition-colors"
                    >
                      {popup.buttonText}
                    </a>
                  )}
                  
                  {popup.dismissable && (
                    <button
                      onClick={handleDismiss}
                      className="px-6 py-3 text-slate-600 hover:text-brand-primary transition-colors font-medium"
                    >
                      Close
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Popup;
